import {Component, h} from 'preact'
import Portal from 'preact-portal'
import tinycolor from 'tinycolor2'
import TextButton from '../ui-components/TextButton'
import Icon from '../assets'
import Button from '../ui-components/Button'
import Colors from './Colors'
import Buttons from './Buttons'
import Cards from './Cards'
import CustomCss from './CustomCss'
import {getColors, getButtons, getCards, getFonts} from './DefaultSettings'
import Axios, {AxiosResponse} from 'axios'
import Fonts from './Fonts'

interface Props {
}

interface State {
  currentTheme: null | {
    type: 'color' | 'button' | 'font' | 'card' | 'customCss',
    text: string
  },
  cards: Array<Object>,
  fonts: Array<Object>,

  defaultColorsStyling: StylingConfigType,
  defaultCardsStyling: StylingConfigType,
  defaultButtonsStyling: StylingConfigType,
  defaultFontsStyling: StylingConfigType,

  existingColorsStyling: StylingConfigType,
  existingButtonsStyling: StylingConfigType,
  existingCardsStyling: StylingConfigType,
  existingFontsStyling: StylingConfigType,

  updatedColorsStyling: StylingConfigType,
  updatedButtonsStyling: StylingConfigType,
  updatedCardsStyling: StylingConfigType,
  updatedFontsStyling: StylingConfigType,

  existingStyling: StylingConfigType,
  updatedStyling: StylingConfigType,
  isLoading: boolean,
  existingCustomCss: string,
  updatedCustomCss: string
}

export type StylingConfigType = {
  [key: string]: string | null
}

interface CustomCssResponse {
  customcss: string
}

const THEME_LIST: Array<State['currentTheme']> = [
  {
    type: 'color',
    text: 'Colors'
  },
  {
    type: 'button',
    text: 'Buttons'
  },
  {
    type: 'font',
    text: 'Fonts'
  },
  {
    type: 'card',
    text: 'Cards'
  },
  {
    type: 'customCss',
    text: 'Custom Css'
  }
]

export default class ThemeConfigurator extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      currentTheme: null,
      cards: [],
      fonts: [],
      isLoading: false,

      defaultButtonsStyling: {},
      defaultColorsStyling: {},
      defaultFontsStyling: {},
      defaultCardsStyling: {},

      existingButtonsStyling: {},
      existingColorsStyling: {},
      existingFontsStyling: {},
      existingCardsStyling: {},

      updatedButtonsStyling: {},
      updatedColorsStyling: {},
      updatedFontsStyling: {},
      updatedCardsStyling: {},

      existingStyling: {},
      updatedStyling: {},
      existingCustomCss: '',
      updatedCustomCss: ''
    }

    this.getComponent = this.getComponent.bind(this)
    this.discardChanges = this.discardChanges.bind(this)
    this.getThemeList = this.getThemeList.bind(this)
    this.updateCommunityStyling = this.updateCommunityStyling.bind(this)
    this.onSave = this.onSave.bind(this)
  }

  CHUNK_SIZE: number = 15

  getThemeList () {
    return (
      <ul className='theme-list'>
        {
          THEME_LIST.map(theme => <li className='theme-item' onClick={() => this.setState({ currentTheme: theme })}>
            <div>{ theme?.text }</div>
            <Icon size={24} color='#2B3346' icon='chevronRight' />
          </li>)
        }
      </ul>
    )
  }

  getComponent () {
    const currentTheme = this.state.currentTheme
    switch (currentTheme?.type) {
      case 'color':
        return <Colors
          defaultStyling={this.state.defaultColorsStyling}
          updatedStyling={this.state.updatedColorsStyling}
          onUpdate={(updatedColorsStyling: StylingConfigType) => this.setState({ updatedColorsStyling: updatedColorsStyling }, this.updateCommunityStyling)}
        />

      case 'button':
        return <Buttons
          defaultStyling={this.state.defaultButtonsStyling}
          updatedStyling={this.state.updatedButtonsStyling}
          onUpdate={(updatedButtonsStyling: StylingConfigType) => this.setState({ updatedButtonsStyling: updatedButtonsStyling }, this.updateCommunityStyling)}
        />

      case 'font':
        return <Fonts
          defaultStyling={this.state.defaultFontsStyling}
          updatedStyling={this.state.updatedFontsStyling}
          onUpdate={(updatedFontsStyling: StylingConfigType) =>
            this.setState({
              updatedFontsStyling: updatedFontsStyling
            }, this.updateCommunityStyling)}/>

      case 'card':
        return <Cards
          defaultStyling={this.state.defaultCardsStyling}
          updatedStyling={this.state.updatedCardsStyling}
          updatedColors={this.state.updatedColorsStyling}
          onUpdate={(updatedCardsStyling: StylingConfigType) => this.setState({ updatedCardsStyling: updatedCardsStyling }, this.updateCommunityStyling)}
        />
      case 'customCss':
        return <CustomCss 
            content={this.state.updatedCustomCss}
            onUpdate={(updatedCustomCss: string) => this.setState({ updatedCustomCss: updatedCustomCss })}
          />
      default:
        return this.getThemeList()
    }
  }

  discardChanges () {
    if (this.state.currentTheme?.type === 'color') {
      this.setState({ updatedColorsStyling: this.state.existingColorsStyling })
    } else if (this.state.currentTheme?.type === 'button') {
      this.setState({ updatedColorsStyling: this.state.existingButtonsStyling })
    } else if (this.state.currentTheme?.type === 'card') {
      this.setState({ updatedCardsStyling: this.state.existingCardsStyling })
    } else if (this.state.currentTheme?.type === 'customCss') {
      this.setState({ updatedCustomCss: this.state.existingCustomCss })
    }
  }

  updateCommunityStyling () {
    this.setState({
      updatedStyling: {
        ...this.state.updatedStyling,
        ...this.state.updatedColorsStyling,
        ...this.state.updatedButtonsStyling,
        ...this.state.updatedFontsStyling,
        ...this.state.updatedCardsStyling
      }
    })
  }

  async onSave () {
    this.setState({ isLoading: true })
    try {
      await Axios.post('/branding/setVariables', {
        ...this.state.updatedStyling
      })

      await Axios.post('/branding/updateCustomCss', {
        data: this.state.updatedCustomCss
      })

      this.setState({ isLoading: false, currentTheme: null })
      window.location.assign('/')
    } catch {
      this.setState({ isLoading: false })
    }
  }

  async componentDidMount() {
    let existingStyling: AxiosResponse<StylingConfigType> = await Axios.get('/branding/variables')
    let existingCustomCss: AxiosResponse<CustomCssResponse> = await Axios.get('/branding/customcss?raw=1')

    if (!existingStyling.data['config-button-border-width']) {
      existingStyling.data['config-button-border-width'] = existingStyling.data['config-button-cta-border-width']
    }
    if (!existingStyling.data['config-button-border-radius']) {
      existingStyling.data['config-button-border-radius'] = existingStyling.data['config-button-cta-border-radius']
    }

    // COLORS
    let defaultColorsStyling: StylingConfigType = getColors()

    const existingColorsStyling: StylingConfigType = {}

    Object.keys(defaultColorsStyling).forEach(key => {
      const color = existingStyling.data[key] || defaultColorsStyling[key]
      existingColorsStyling[key] = color !== null ? tinycolor(existingStyling.data[key] || defaultColorsStyling[key]).toHex8String() : color
    })

    // BUTTONS
    let defaultButtonsStyling: StylingConfigType = getButtons(existingStyling.data)

    const existingButtonsStyling: StylingConfigType = {}

    Object.keys(defaultButtonsStyling).forEach(key => {
      if (key === 'config--main-button-base-font-weight' && (existingStyling.data[key] || defaultButtonsStyling[key]) === 'normal') {
        existingButtonsStyling[key] = '400'
      } else {
        existingButtonsStyling[key] = existingStyling.data[key] || defaultButtonsStyling[key]
      }
    })

    // CARDS
    let defaultCardsStyling: StylingConfigType = getCards()

    const existingCardsStyling: StylingConfigType = {}

    Object.keys(defaultCardsStyling).forEach(key => {
      existingCardsStyling[key] = existingStyling.data[key] || defaultCardsStyling[key]
    })

    // FONTS
    let defaultFontsStyling: StylingConfigType = getFonts()

    const existingFontsStyling: StylingConfigType = {}

    Object.keys(defaultFontsStyling).forEach(key => {
      existingFontsStyling[key] = existingStyling.data[key] || defaultFontsStyling[key]
    })

    this.setState({
      defaultButtonsStyling: defaultButtonsStyling,
      updatedButtonsStyling: existingButtonsStyling,
      existingButtonsStyling: existingButtonsStyling,

      defaultColorsStyling: defaultColorsStyling,
      updatedColorsStyling: existingColorsStyling,
      existingColorsStyling: existingColorsStyling,

      defaultCardsStyling: defaultCardsStyling,
      updatedCardsStyling: existingCardsStyling,
      existingCardsStyling: existingCardsStyling,

      defaultFontsStyling: defaultFontsStyling,
      updatedFontsStyling: existingFontsStyling,
      existingFontsStyling: existingFontsStyling,

      existingStyling: existingStyling.data,
      updatedStyling: existingStyling.data,
      existingCustomCss: existingCustomCss.data.customcss,
      updatedCustomCss: existingCustomCss.data.customcss
    })
  }

  render (_: Props, { currentTheme, isLoading, updatedStyling, updatedCustomCss }: State) {
    let latestCommunityStyle: Array<string> = Object.keys(updatedStyling)
      .filter(configKey => updatedStyling[configKey])
      .map(configKey => {
        return `--${configKey}: ${updatedStyling[configKey]}`
      })

    return <Portal into='body'>
      <div className='theme-configurator'>
        <style>
          {`
            html {
              ${latestCommunityStyle.join(';')}
            }
            ${updatedCustomCss}
          `}
        </style>
        <div className='theme-configurator--header'>
          <div className='theme-configurator--header__left'>
            {
              currentTheme !== null ? (
                <div className='theme-configurator--header__back' onClick={() => {this.setState({ currentTheme: null })}}>
                  <Icon size={24}  icon='chevronLeft' />
                </div>
              ) : null
            }
            <h4 className='theme-configurator--header-title'>{ currentTheme === null ? 'THEME' : currentTheme.text }</h4>
          </div>
          <TextButton style={{ padding: '4px', display: 'flex', borderRadius: '2px' }} class='close-btn ' onClick={() => {this.setState({ currentTheme: null })}} >
            <Icon icon='close' />
          </TextButton>
        </div>
        <div className={`theme-configurator--body`}>
          { this.getComponent() }
        </div>
        <div class='theme-configurator--footer'>
          {
            currentTheme !== null ? (
              <div className='theme-configurator--buttons'>
                <Button insided onClick={() => this.setState({currentTheme: null})}>Done</Button>
                <Button insidedSecondary onClick={this.discardChanges}>Discard changes</Button>
              </div>
            ) : (
              <Button insided loading={isLoading} onClick={this.onSave}>Publish</Button>
            )
          }
        </div>
      </div>
    </Portal>
  }
}
